import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TierApi,
  TierItem,
  InsertUpdateTiersRequest,
} from "@/api/giftcard.api";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";

const TIER_TYPE_OPTIONS = [
  { value: "TxnCount", label: "Txn Count" },
  { value: "CummulativeSpend", label: "Cumulative Spend" },
] as const;

const STATIC_CLIENT_ID = "1009";

const createTierSchema = z
  .object({
    tierName: z.string().trim().min(1, "Tier name is required"),
    tierType: z.enum(["TxnCount", "CummulativeSpend"], {
      required_error: "Tier type is required",
    }),
    fromValue: z.coerce.number({
      required_error: "From value is required",
      invalid_type_error: "From value must be a number",
    }),
    toValue: z.coerce.number({
      required_error: "To value is required",
      invalid_type_error: "To value must be a number",
    }),
    percentage: z.coerce
      .number({
        required_error: "Percentage is required",
        invalid_type_error: "Percentage must be a number",
      })
      .min(0, "Percentage cannot be negative"),
  })
  .refine((data) => data.toValue >= data.fromValue, {
    message: "To value must be greater than or equal to From value",
    path: ["toValue"],
  });

type CreateTierFormValues = z.infer<typeof createTierSchema>;

function normalizeTierType(
  value?: string | null
): CreateTierFormValues["tierType"] | undefined {
  if (value === "TxnCount" || value === "CummulativeSpend") {
    return value;
  }
  return undefined;
}

interface CreateTierFormProps {
  tier?: TierItem | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function CreateTierForm({
  tier,
  onSuccess,
  onCancel,
}: CreateTierFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditMode = Boolean(tier?.id);

  const form = useForm<CreateTierFormValues>({
    resolver: zodResolver(createTierSchema),
    defaultValues: {
      tierName: tier?.tierName || "",
      tierType: normalizeTierType(tier?.tierType),
      fromValue: (tier?.fromValue ?? undefined) as unknown as number,
      toValue: (tier?.toValue ?? undefined) as unknown as number,
      percentage: (tier?.percentage ?? undefined) as unknown as number,
    },
  });

  useEffect(() => {
    form.reset({
      tierName: tier?.tierName || "",
      tierType: normalizeTierType(tier?.tierType),
      fromValue: (tier?.fromValue ?? undefined) as unknown as number,
      toValue: (tier?.toValue ?? undefined) as unknown as number,
      percentage: (tier?.percentage ?? undefined) as unknown as number,
    });
  }, [tier, form]);

  const onSubmit = async (data: CreateTierFormValues) => {
    setIsSubmitting(true);
    try {
      const userInfoRaw = sessionStorage.getItem("userInfo");
      const parsedUserInfo = userInfoRaw ? JSON.parse(userInfoRaw) : null;
      const userId = Number(parsedUserInfo?.userId) || 0;

      const payload: InsertUpdateTiersRequest = {
        p_TierName: data.tierName.trim(),
        p_TierType: data.tierType,
        p_FromValue: data.fromValue,
        p_ToValue: data.toValue,
        p_Percentage: data.percentage,
        p_ClientId: tier?.clientId || STATIC_CLIENT_ID,
      };

      if (isEditMode && tier?.id) {
        payload.p_Id = tier.id;
        payload.p_UpdatedBy = userId;
      } else {
        payload.p_CreatedBy = userId;
      }

      const response = await TierApi.insertUpdateTiers(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description:
          response.message ||
          (isEditMode
            ? "Tier updated successfully."
            : "Tier created successfully."),
      });
      if (!isEditMode) {
        form.reset();
      }
      onSuccess?.();
    } catch (error) {
      console.error(
        isEditMode ? "Error updating tier:" : "Error creating tier:",
        error
      );
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description:
          errorMessage ||
          (isEditMode ? "Failed to update tier" : "Failed to create tier"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="tierName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tier Name *</FormLabel>
              <FormControl>
                <Input placeholder="Enter tier name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="tierType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tier Type *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select tier type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {TIER_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="fromValue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>From Value *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="any"
                    placeholder="e.g. 1"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="toValue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>To Value *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="any"
                    placeholder="e.g. 3"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="percentage"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Percentage *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="any"
                  placeholder="e.g. 2"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-green-600 text-white hover:bg-green-700"
          >
            {isSubmitting
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update Tier"
                : "Create Tier"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
